import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { FlaskConical, BookOpen, Satellite, TreePine } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';

const ICONS = [TreePine, Satellite, FlaskConical, BookOpen];

export default function ResearchSection() {
  const { t } = useLanguage();
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' });
  const topics = t('research.topics');

  return (
    <section id="research" className="relative py-24 sm:py-32 px-6 sm:px-12 lg:px-24 overflow-hidden" data-testid="research-section">
      <div className="relative max-w-6xl mx-auto">
        <motion.div
          ref={sectionRef}
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="mb-16"
        >
          <h2 className="text-3xl sm:text-4xl tracking-tight font-medium max-w-3xl">
            {t('research.heading')}{' '}
            <span className="text-[#BDD6B5]">{t('research.headingGreen')}</span>
          </h2>
          <p className="mt-4 text-base text-white/78 max-w-2xl leading-relaxed">
            {t('research.subtitle')}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {Array.isArray(topics) && topics.map((topic, i) => {
            const Icon = ICONS[i];
            const TopicCard = () => {
              const ref = useRef(null);
              const inView = useInView(ref, { once: true, margin: '-60px' });
              return (
                <motion.div
                  ref={ref}
                  initial={{ opacity: 0, y: 40 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.7, delay: i * 0.1 }}
                  className="glass-card p-8 group relative overflow-hidden"
                  data-testid={`research-card-${i}`}
                >
                  <div className="absolute -top-12 -right-12 w-24 h-24 bg-[#BDD6B5]/5 rounded-full blur-2xl group-hover:bg-[#BDD6B5]/10 transition-colors duration-500" />
                  <Icon size={24} className="text-[#A4C3D2] mb-4" strokeWidth={1.5} />
                  <h3 className="text-lg font-semibold text-white mb-3">{topic.title}</h3>
                  <p className="text-sm text-white/78 leading-relaxed">{topic.desc}</p>
                  <div className="mt-4 pt-4 border-t border-[#3D5638]/20">
                    <span className="text-xs font-mono text-[#A4C3D2]/40 tracking-widest uppercase">
                      {t('research.pending')}
                    </span>
                  </div>
                </motion.div>
              );
            };
            return <TopicCard key={i} />;
          })}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4, duration: 0.8 }}
          className="mt-12 glass-card p-8 text-center border-[#3D5638]/20 border-dashed"
        >
          <p className="text-sm text-white/40 leading-relaxed max-w-xl mx-auto">
            {t('research.disclaimer')}
          </p>
        </motion.div>
      </div>
    </section>
  );
}
